import type { ReactNode } from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { MinidownContent } from '@/components/MinidownContent'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import { match } from '@/utils/pattern-match-util'
import styles from './QuestionPreview.module.css'

export interface QuestionPreviewProps {
  question: string
  description?: string
  /**
   * How the client answers, like the options to pick from.
   */
  answer?: ReactNode
}

/**
 * A question as the client sees it, on its card.
 */
export function QuestionPreview({
  question,
  description,
  answer,
}: QuestionPreviewProps) {
  return (
    <div className={styles.root}>
      <p className={cx(styles.question, !question && styles.muted)}>
        {question || 'No question text'}
      </p>
      {description && (
        <div className={styles.description}>
          <MinidownContent value={description} />
        </div>
      )}
      {answer && <div className={styles.answer}>{answer}</div>}
    </div>
  )
}

/**
 * A labelled entry of a preview list, like a select's option.
 */
export interface QuestionPreviewItem {
  key: string
  label: string
}

export interface QuestionPreviewPillsProps {
  items: readonly QuestionPreviewItem[]
  /**
   * Shows that several can be picked.
   */
  isMulti?: boolean
  /**
   * Shown when there are no labels.
   */
  emptyText?: string
}

/**
 * Answers to pick from, like a select's options.
 */
export function QuestionPreviewPills({
  items,
  isMulti,
  emptyText,
}: QuestionPreviewPillsProps) {
  if (items.length === 0) {
    return <QuestionPreviewMutedText>{emptyText}</QuestionPreviewMutedText>
  }
  return (
    <div className={styles.pills}>
      {isMulti && (
        <Icon
          name="list-view"
          size={14}
          className={styles.multiIcon}
          role="img"
          aria-label="Multiple answers"
        />
      )}
      {items.map((item) => (
        <span key={item.key} className={styles.pill}>
          {item.label}
        </span>
      ))}
    </div>
  )
}

export interface QuestionPreviewInputProps {
  text: string
  variant?: 'text' | 'numeric' | 'date'
}

/**
 * A pretend input with a sample answer.
 */
export function QuestionPreviewInput({
  text,
  variant = 'text',
}: QuestionPreviewInputProps) {
  return (
    <span
      className={cx(
        styles.input,
        match(variant, {
          text: undefined,
          numeric: styles.numeric,
          date: styles.date,
        }),
      )}
    >
      <span>{text}</span>
      {variant === 'date' && (
        <Icon name="history" size={14} aria-hidden="true" />
      )}
    </span>
  )
}

export interface QuestionPreviewButtonProps {
  label: string
}

/**
 * A pretend button, like a breaker's.
 */
export function QuestionPreviewButton({ label }: QuestionPreviewButtonProps) {
  return <span className={styles.button}>{label}</span>
}

export interface QuestionPreviewDocumentsProps {
  items: readonly QuestionPreviewItem[]
}

/**
 * The documents a documents question requests.
 */
export function QuestionPreviewDocuments({
  items,
}: QuestionPreviewDocumentsProps) {
  if (items.length === 0) {
    return (
      <QuestionPreviewMutedText>
        No documents requested yet
      </QuestionPreviewMutedText>
    )
  }
  return (
    <ul className={styles.documents}>
      {items.map((item) => (
        <li key={item.key} className={styles.document}>
          <Icon name="documents" size={14} aria-hidden="true" />
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  )
}

interface QuestionPreviewMutedTextProps {
  children?: ReactNode
}

function QuestionPreviewMutedText({ children }: QuestionPreviewMutedTextProps) {
  return <span className={styles.muted}>{children}</span>
}

/**
 * A skeleton for a {@link QuestionPreview}, with a couple of answers to pick
 * from.
 */
export function QuestionPreviewSkeleton() {
  const { vary } = useSkeletonListContext()
  return (
    <div className={styles.root}>
      <p className={styles.question}>
        <Skeleton width={vary(160)} />
      </p>
      <div className={styles.answer}>
        <div className={styles.pills}>
          {[64, 80].map((width) => (
            <Skeleton
              key={width}
              variant="rect"
              width={vary(width)}
              height={30}
              borderRadius={15}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
